import { useState } from 'react';
import { useFadeIn } from '@/hooks/useFadeIn';

interface TicketProps {
  onImageClick: (imageUrl: string) => void;
  onVenueImageClick: (imageUrl: string) => void;
}

export default function Ticket({ onImageClick, onVenueImageClick }: TicketProps) {
  const [openNotice, setOpenNotice] = useState(false);
  const [openTokyo, setOpenTokyo] = useState(false);
  const [openOsaka, setOpenOsaka] = useState(false);
  const [openFukuoka, setOpenFukuoka] = useState(false);

  // 小さなブロックごとのフェードインフック
  const blockTicket1 = useFadeIn(0);
  const blockTicket2 = useFadeIn(80);
  const blockTicket3 = useFadeIn(160);
  const blockTicket4 = useFadeIn(240);
  const blockTicket5 = useFadeIn(320);
  const blockNotes1 = useFadeIn(400);
  const blockNotes2 = useFadeIn(480);
  const blockNotes3 = useFadeIn(560);
  const blockNotice = useFadeIn(640);
  const blockVenueMap = useFadeIn(0);
  const blockVenueNote = useFadeIn(80);
  const blockBonus = useFadeIn(0);
  const blockRepeatTicket = useFadeIn(0);

  const fadeClass = (hook: ReturnType<typeof useFadeIn>) =>
    `transition-all duration-[800ms] ease-out ${hook.isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-[0.97]'}`;

  const venueImageUrl = 'https://www.irinomiyu-uuu.com/img/goods02_b97beb0ddd7.png';

  const openVenueModal = (src: string) => {
    if (window.innerWidth < 768) return;
    onVenueImageClick(src);
  };

  const ticketTypes = [
    {
      name: 'VIPチケット',
      type: '自由席',
      price: '19,800円（税込）',
      features: [
        'ステージ前方エリアでご覧いただけます',
        '特典グッズ付き：\nスタッフTシャツ(会場毎にデザイン共通でカラーが異なります)\nニットバッグ(共通デザイン)\nVIPパスステッカー(会場毎の限定デザイン)',
        'CD予約先行（CMS早期予約特典）とオフィシャル1次のみの販売'
      ],
      featuresMobile: [
        { lines: ['ステージ前方エリアでご覧いただけます'] },
        {
          lines: [
            '特典グッズ付き：',
            'スタッフTシャツ(会場毎にデザイン共通でカラーが異なります)',
            'ニットバッグ(共通デザイン)',
            'VIPパスステッカー(会場毎の限定デザイン)'
          ]
        },
        { lines: ['CD予約先行（CMS早期予約特典）と', 'オフィシャル1次のみの販売'] }
      ],
      color: '#fc457e',
      isVip: true
    },
    {
      name: 'グッズ付き指定席',
      type: '',
      price: '15,000円（税込）',
      features: [
        '特典グッズ付き：ニットバック（非売品）',
        'CD予約先行（CMS早期予約特典）とオフィシャル1次のみの販売'
      ],
      featuresMobile: [
        { lines: ['特典グッズ付き：ニットバック（非売品）'] },
        { lines: ['CD予約先行（CMS早期予約特典）と', 'オフィシャル1次のみの販売'] }
      ],
      color: '#44c4ff'
    },
    {
      name: 'グッズ付きチケット',
      type: '自由席',
      price: '14,000円（税込）',
      features: [
        '特典グッズ付き：ニットバック（非売品）',
        'CD予約先行（CMS早期予約特典）とオフィシャル1次のみの販売'
      ],
      featuresMobile: [
        { lines: ['特典グッズ付き：ニットバック（非売品）'] },
        { lines: ['CD予約先行（CMS早期予約特典）と', 'オフィシャル1次のみの販売'] }
      ],
      color: '#44c4ff'
    },
    {
      name: '指定席',
      type: '',
      price: '10,000円（税込）',
      features: [],
      featuresMobile: [],
      color: '#44c4ff'
    },
    {
      name: '自由席',
      type: '',
      price: '9,000円（税込）',
      features: [],
      featuresMobile: [],
      color: '#44c4ff'
    }
  ];

  const ticketBlocks = [blockTicket1, blockTicket2, blockTicket3, blockTicket4, blockTicket5];

  const venues = [
    {
      name: 'Kanadevia Hall',
      image: 'https://www.irinomiyu-uuu.com/img/00e04982039c007b53a835080deaba54.png'
    },
    {
      name: 'Zepp Osaka Bayside',
      image: 'https://www.irinomiyu-uuu.com/img/a8fe75ba9440fbedf653ce097d8815f8.png'
    },
    {
      name: 'Zepp Fukuoka',
      image: 'https://www.irinomiyu-uuu.com/img/4ebe5347239505e8e60208afefca0e09.png'
    },
    {
      name: 'Zepp Haneda (TOKYO)',
      image: 'https://www.irinomiyu-uuu.com/img/b4dbe8ebd58b13943ed3010c1985b7f7.png'
    }
  ];

  return (
    <section
      id="ticket"
      className="py-20"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div className="text-center mb-16">
          <div className="inline-block bg-[#fcb122] px-8 md:px-12 py-3 md:py-4 shadow-[0_0_40px_#fcb122]">
            <h2 className="text-3xl md:text-5xl text-[#0a0a0a] font-bold">TICKET</h2>
          </div>
        </div>

        <div className="grid gap-6 mb-8">
          {ticketTypes.map((ticket, index) => (
            <div
              key={index}
              ref={ticketBlocks[index].ref}
              className={`bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-4 p-4 md:p-8 hover:scale-105 transition-all duration-300 hover:shadow-[0_0_40px_var(--glow-color)] ${fadeClass(ticketBlocks[index])}`}
              style={{
                borderColor: ticket.color,
                boxShadow: `0 0 30px ${ticket.color}60`,
                '--glow-color': ticket.color
              } as React.CSSProperties}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3">
                    {ticket.isVip && <span className="text-xl md:text-2xl">👑</span>}
                    <h3 className="text-base md:text-2xl text-white font-bold break-words">
                      {ticket.name}
                      {ticket.type && (
                        <span className="block md:inline text-xs md:text-base text-white/70 md:ml-2 break-words">
                          （{ticket.type}）
                        </span>
                      )}
                    </h3>
                  </div>
                </div>
                <div
                  className="px-4 md:px-6 py-2 md:py-3 border-2 text-center font-bold shrink-0"
                  style={{
                    backgroundColor: ticket.color,
                    borderColor: ticket.color,
                    color: '#0a0a0a',
                    boxShadow: `0 0 20px ${ticket.color}`
                  }}
                >
                  <span className="text-sm md:text-xl whitespace-nowrap">{ticket.price}</span>
                </div>
              </div>

              {ticket.features.length > 0 && (
                <>
                  <div className="hidden md:block space-y-2 text-xs md:text-sm text-white/90">
                    {ticket.features.map((feature, idx) => (
                      <p key={idx} className="flex items-start gap-2">
                        <span style={{ color: ticket.color }} className="font-dotgothic shrink-0">
                          ▷
                        </span>
                        <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>{feature}</span>
                      </p>
                    ))}
                  </div>

                  <div className="md:hidden space-y-2 text-xs text-white/90">
                    {ticket.featuresMobile.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span style={{ color: ticket.color }} className="font-dotgothic shrink-0">
                          ▷
                        </span>
                        <div className="break-words overflow-wrap-anywhere min-w-0 flex-1">
                          {feature.lines.map((line, lineIdx) => (
                            <p key={lineIdx} className="break-words">{line}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* 注意事項 */}
        <div className="space-y-4 mb-16">
          <div ref={blockNotes1.ref} className={`bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4 ${fadeClass(blockNotes1)}`}>
            <p className="text-xs md:text-sm text-white/70 break-words">
              ※グッズのお渡しは当日会場のみとなります。ご来場いただけなかった場合の後日お渡しや個別での発送対応はお受けできません。あらかじめご了承ください。
            </p>
          </div>
          <div ref={blockNotes2.ref} className={`bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4 ${fadeClass(blockNotes2)}`}>
            <p className="hidden md:block text-xs md:text-sm text-white/70 break-words">
              <strong className="text-[#44c4ff]">枚数制限：</strong>お一人様1公演につき4枚まで（複数公演申込可）
            </p>
            <p className="md:hidden text-xs text-white/70 break-words">
              <strong className="text-[#44c4ff]">枚数制限：</strong>お一人様1公演につき4枚まで<br />
              （複数公演申込可）
            </p>
          </div>
          <div ref={blockNotes3.ref} className={`bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4 ${fadeClass(blockNotes3)}`}>
            <p className="text-xs md:text-sm text-white/70 break-words">
              <strong className="text-[#44c4ff]">ドリンク代について：</strong>ご入場時に別途ドリンク代を現地にてお支払いいただきます。
            </p>
            <ul className="mt-2 space-y-1 text-xs md:text-sm text-white/70 pl-2">
              <li>東京ドームシティ kanadeviaホール：500円(税込)</li>
              <li>Zepp Osaka Bayside：600円(税込)</li>
              <li>Zepp Fukuoka：600円(税込)</li>
              <li>Zepp Haneda：600円(税込)</li>
            </ul>
          </div>
        </div>

        {/* 公演に関するお知らせ */}
        <div ref={blockNotice.ref} className={`mb-8 border-2 border-[#fc457e] overflow-hidden ${fadeClass(blockNotice)}`}>
          <button
            onClick={() => setOpenNotice(prev => !prev)}
            className="w-full flex items-center justify-between px-4 md:px-6 py-4 text-left cursor-pointer hover:bg-white/5 transition-colors bg-[#1a1a3e]/80"
          >
            <span className="text-sm md:text-base text-white font-bold">公演に関するお知らせ</span>
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <span className={`text-[#fc457e] text-xl transition-transform duration-300 font-dotgothic ${openNotice ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </div>
          </button>
          <div className={`transition-all duration-300 overflow-hidden ${openNotice ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="px-4 md:px-6 py-5 bg-[#1a1a3e]/60 space-y-4">
              <p className="text-xs md:text-sm text-white/90 leading-relaxed whitespace-pre-line">{`本公演におきまして、会場レイアウトおよび観覧環境の最適化を目的とし、\n客席構成を一部変更させていただくこととなりました。\n\nこれに伴い、スタンディング席を自由席（椅子席）へ変更させていただきます。\n\n当日はチケットに記載の整理番号順でのご入場後、\nお好きな席をお選びいただく形でのご案内となります。\n\n本変更により、より快適にご覧いただける環境をご提供できるよう努めてまいります。`}</p>
              <div className="border-t border-[#fc457e]/30 pt-4 space-y-1">
                <p className="text-xs md:text-sm text-[#fc457e]/90 leading-relaxed">※荷物だけ置いての席取りはご遠慮ください。</p>
                <p className="text-xs md:text-sm text-[#fc457e]/90 leading-relaxed">複数席の確保（お連れ様の席取り）は禁止です。</p>
              </div>
            </div>
          </div>
        </div>

        {/* 会場図面 */}
        <div ref={blockVenueMap.ref} className={`mb-16 ${fadeClass(blockVenueMap)}`}>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {venues.map((venue, index) => (
              <li key={index} className="border-4 border-[#44c4ff] bg-white w-full">
                <div className="w-full flex flex-col items-center justify-start p-4">
                  <div className="h-12 flex items-center justify-center w-full">
                    <h3 className="text-sm md:text-base text-[#0a0a0a] font-bold text-center break-words leading-tight">{venue.name}</h3>
                  </div>
                  <p className="text-xs text-[#0a0a0a] text-center mb-4">【自由席図】</p>
                  <div className="w-full flex items-start justify-center">
                    <img
                      src={venue.image}
                      alt={`${venue.name}の会場図`}
                      className="w-full h-auto object-contain object-top md:cursor-pointer md:hover:opacity-80 transition-opacity duration-200"
                      onClick={() => openVenueModal(venue.image)}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div ref={blockVenueNote.ref} className={`bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4 mt-6 ${fadeClass(blockVenueNote)}`}>
            <p className="text-xs md:text-sm text-white/70 break-words">
              ※2025年12月19日（金）時点での座席レイアウト・イメージとなります。<br />
              ※ステージプラン及び、各席種の販売状況次第で、実際の座席レイアウトが予告なく変更となる場合がございます。<br />
              ※座席レイアウト変更に伴う、返金対応・振替対応等はいたしかねます。予めご了承の上でご購入ください。
            </p>
          </div>
        </div>

        {/* 来場者特典 */}
        <div ref={blockBonus.ref} className={`bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-4 border-[#1ef99d] p-6 md:p-8 shadow-[0_0_40px_#1ef99d60] ${fadeClass(blockBonus)}`}>
          <div className="text-center mb-8">
            <h3 className="text-xl md:text-2xl text-[#1ef99d] mb-4 flex items-center justify-center gap-3 font-bold">
              <span className="font-dotgothic">☆</span>
              <span>来場者特典</span>
              <span className="font-dotgothic">☆</span>
            </h3>
          </div>

          <div className="mb-8 flex justify-center">
            <img
              src={venueImageUrl}
              alt="来場者特典グッズ画像"
              className="w-full max-w-md md:max-w-lg h-auto rounded-lg md:cursor-pointer md:hover:opacity-90 transition-opacity duration-300 md:hover:shadow-[0_0_20px_#1ef99d]"
              onClick={() => {
                if (window.innerWidth >= 768) {
                  onImageClick(venueImageUrl);
                }
              }}
            />
          </div>

          <div className="bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-4 md:px-6 py-4">
            <p className="text-xs md:text-sm text-white/70 break-words">
              ※各会場毎に異なるデザインになります。<br />
              ※黒抜き部分は写真が入ります。写真の絵柄は当日のお楽しみ！<br />
              ※来場者特典のお渡しは当日会場のみとなります。ご来場いただけなかった場合の後日お渡しや個別での発送対応はお受けできません。あらかじめご了承ください。
            </p>
          </div>
        </div>

        {/* リピーターチケット販売に関するお知らせ */}
        <div ref={blockRepeatTicket.ref} className={`mt-8 border-2 border-[#fc457e] overflow-hidden ${fadeClass(blockRepeatTicket)}`}>
          <div className="px-4 md:px-6 py-4 bg-[#1a1a3e]/80 text-center">
            <span className="text-xl md:text-2xl text-[#fc457e] font-bold leading-snug">
              「IRINO MIYU LIVE TOUR2026」<br />
              リピーターチケット販売に関するお知らせ
            </span>
          </div>
          <div className="px-4 md:px-6 py-5 bg-[#1a1a3e]/60 space-y-3">

            {/* 東京公演アコーディオン */}
            <div className="border border-[#fc457e]/50 overflow-hidden">
              <button
                onClick={() => setOpenTokyo(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors bg-[#1a1a3e]/80"
              >
                <span className="text-sm md:text-base text-white font-bold">東京公演</span>
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                  <span className={`text-[#fc457e] text-base transition-transform duration-300 font-dotgothic ${openTokyo ? 'rotate-180' : ''}`}>▼</span>
                </div>
              </button>
              <div className={`transition-all duration-300 overflow-hidden ${openTokyo ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-4 py-4 bg-[#1a1a3e]/40 space-y-4 text-xs md:text-sm text-white/90 leading-relaxed">
                  <p>4/13(月)東京ドームシティkanadeviaホール『IRINO MIYU LIVE TOUR 2026』にご来場いただいた方を対象に、特典会付きリピーターチケットを販売いたします。ご購入特典として、入野自由とのタッチ会に参加いただけます。<br />
                  リピーターチケットの販売は、会場チケット販売窓口の「羽田公演チケット受付」にて行います。</p>
                  <p className="text-[#fc457e]/90">※タッチ会付きリピーターチケット販売は大阪公演、福岡公演でも実施予定となります。詳細は後日解禁させていただきます。</p>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●チケット料金：自由席9,000円（税込）</p>
                    <p className="text-[#fc457e]/90">※リピータ―チケットは自由席のみのお取り扱いとなります</p>
                    <p className="text-[#fc457e]/90">※チケット券種はお選びいただけません。</p>
                    <p className="text-[#fc457e]/90">※本チケットのご購入は現金のみとなります。クレジットカードはご使用いただけません。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●リピーターチケットにつきまして</p>
                    <p>当日16時～予定販売数終了まで「羽田公演チケット販売ブーズ」にてご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※4/13(月)東京ドームシティkanadevia公演にご参加の方のみご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※お一人様2枚までご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※ブースでのチケット販売は数量限定となります。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●ご購入特典</p>
                    <p>タッチ会</p>
                    <p className="text-[#fc457e]/90">※お一人様1枚につき、1回ご参加いただけます</p>
                    <p className="text-[#fc457e]/90">※人数には限りがございますので、上限を超えた場合は先着となります。予めご了承下さい。</p>
                    <p className="text-[#fc457e]/90">※4/13開催の東京公演（東京ドームシティkanadeviaホール）にご参加の方で、既に羽田公演のチケットをご購入いただいている方もご参加いただけます。大阪公演、福岡公演でも同施策の実施を予定しておりますが、Kanadeviaホールでご参加済みのお客様は大阪、福岡公演でのタッチ会のみのご参加は出来かねます。ご了承ください。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">＜特典会 参加方法＞</p>
                    <p>集合時間：終演後当日指定の場所にお集まりください。必ず20:30までにはご整列をお願いいたします。</p>
                    <p>実施時間：整列が完了次第タッチ会開始～実施時間約30分を予定</p>
                    <p>終了予定時間：21:00頃予定</p>
                    <p className="text-[#fc457e]/90">※状況により終了予定時間が前後する可能性がございます。予めご了承くださいませ。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">＜特典会　開催場所＞</p>
                    <p>当日スタッフよりお知らせさせていただきます</p>
                    <p className="text-[#fc457e]/90">※特典会へのご参加されるお客様は、集合時間よりスタッフの指示に従って整列ください。</p>
                    <p className="text-[#fc457e]/90">※スタッフに、公演チケット・ご購入画面をご提示いただきます。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">【注意事項】</p>
                    <p className="text-[#fc457e]/90">※4/13開催の東京公演（東京ドームシティkanadeviaホール）にご参加の方で、既に羽田公演のチケットをご購入いただいている方もご参加いただけます。</p>
                    <p className="text-[#fc457e]/90">※4/13(月)東京ドームシティKanadeviaホール公演にご来場のお客様のみご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※参加者多数の場合は、お時間の都合上途中で終了となることがございます。</p>
                    <p className="text-[#fc457e]/90">※特典会に参加されないお客様も多数おられます。どうぞご留意願います。</p>
                    <p className="text-[#fc457e]/90">※通行のためのスペース確保にご協力下さい。</p>
                    <p className="text-[#fc457e]/90">※イベント中スタッフがお客様の肩や腕に触れて誘導する場合がございます。</p>
                    <p className="text-[#fc457e]/90">※イベント会場へのお問い合わせはご遠慮下さい。</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 大阪公演アコーディオン */}
            <div className="border border-[#fc457e]/50 overflow-hidden">
              <button
                onClick={() => setOpenOsaka(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors bg-[#1a1a3e]/80"
              >
                <span className="text-sm md:text-base text-white font-bold">大阪公演</span>
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                  <span className={`text-[#fc457e] text-base transition-transform duration-300 font-dotgothic ${openOsaka ? 'rotate-180' : ''}`}>▼</span>
                </div>
              </button>
              <div className={`transition-all duration-300 overflow-hidden ${openOsaka ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-4 py-4 bg-[#1a1a3e]/40 space-y-4 text-xs md:text-sm text-white/90 leading-relaxed">
                  <p>4/17(金)Zepp　Osaka　Bayside『IRINO MIYU LIVE TOUR 2026』にご来場いただいた方を対象に、特典会付き羽田公演リピーターチケットを販売いたします。ご購入特典として、入野自由とのタッチ会に参加いただけます。<br />
                  リピーターチケットの販売は、会場チケット販売窓口の「羽田公演チケット受付」にて行います。</p>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●チケット料金：１階 自由席9，０００円（税込）</p>
                    <p className="text-[#fc457e]/90">※リピータ―チケットは１階 自由席のみのお取り扱いとなります</p>
                    <p className="text-[#fc457e]/90">※チケット券種はお選びいただけません。</p>
                    <p className="text-[#fc457e]/90">※本チケットのご購入は現金のみとなります。クレジットカードはご使用いただけません。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●リピーターチケットにつきまして</p>
                    <p>当日16時～予定販売数終了まで「羽田公演チケット販売ブース」にてご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※4/17(金) Zepp　Osaka　Bayside公演にご参加の方のみご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※お一人様ご同行者分含め２枚までご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※ブースでのチケット販売は数量限定となります。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●ご購入特典</p>
                    <p>タッチ会</p>
                    <p className="text-[#fc457e]/90">※お一人様1枚につき、1回ご参加いただけます。</p>
                    <p className="text-[#fc457e]/90">※おひとり様1回でのご参加をお願いしておりますので、並びなおしでの複数回参加などはお断りいたします。</p>
                    <p className="text-[#fc457e]/90">※人数には限りがございますので、上限を超えた場合は先着となります。予めご了承下さい。</p>
                    <p className="text-[#fc457e]/90">※4/17(金) Zepp　Osaka　Baysideにご参加の方で、既に羽田公演の前売りチケットをご購入いただいている方もチケットのご購入履歴が分かる各チケット販売サイトの購入履歴画面や、入金が確認できるメール画面などご提示いただければタッチ会へご参加いただけます。</p>
                    <p className="text-[#fc457e]/90">※羽田公演のチケットをご同行者が購入し、ご自身のお手元に購入証明がないお客様に関しましては購入履歴が分かる画面のスクリーンショットでも対応可能とさせていただきますのでお手元にご用意の上、ご参加をお願いいたします。</p>
                    <p className="text-[#fc457e]/90">※前売りでご購入済みの羽田公演チケット1枚でKanadeviaホール、Zepp Osaka Bayside、Zepp Fukuokaいずれかの会場でタッチ会に１回参加可能となりますので同じチケット1枚で複数の会場でのタッチ会への参加はできません。</p>
                    <p className="text-[#fc457e]/90">※前売りの購入履歴を提示し、Kanadeviaホールで既にタッチ会にご参加いただいたお客様はZepp Osaka Bayside、Zepp Fukuokaでの参加はできません。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">＜特典会 参加方法＞</p>
                    <p>集合時間：終演後当日指定の場所にお集まりください。必ず20:30までにはご整列をお願いいたします。</p>
                    <p>実施時間：整列が完了次第タッチ会開始～実施時間約30分を予定</p>
                    <p>終了予定時間：21:00頃予定</p>
                    <p className="text-[#fc457e]/90">※状況により終了予定時間が前後する可能性がございます。予めご了承くださいませ。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">＜特典会　開催場所＞当日スタッフよりお知らせさせていただきます</p>
                    <p className="text-[#fc457e]/90">※特典会へのご参加されるお客様は、集合時間よりスタッフの指示に従って整列ください。</p>
                    <p className="text-[#fc457e]/90">※スタッフに、公演チケット・ご購入画面をご提示いただきます。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">【注意事項】</p>
                    <p className="text-[#fc457e]/90">※参加者多数の場合は、お時間の都合上途中で終了となることがございます。</p>
                    <p className="text-[#fc457e]/90">※特典会に参加されないお客様も多数おられます。どうぞご留意願います。</p>
                    <p className="text-[#fc457e]/90">※通行のためのスペース確保にご協力下さい。</p>
                    <p className="text-[#fc457e]/90">※イベント中スタッフがお客様の肩や腕に触れて誘導する場合がございます。</p>
                    <p className="text-[#fc457e]/90">※イベント会場へのお問い合わせはご遠慮下さい。</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 福岡公演アコーディオン */}
            <div className="border border-[#fc457e]/50 overflow-hidden">
              <button
                onClick={() => setOpenFukuoka(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors bg-[#1a1a3e]/80"
              >
                <span className="text-sm md:text-base text-white font-bold">福岡公演</span>
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                  <span className={`text-[#fc457e] text-base transition-transform duration-300 font-dotgothic ${openFukuoka ? 'rotate-180' : ''}`}>▼</span>
                </div>
              </button>
              <div className={`transition-all duration-300 overflow-hidden ${openFukuoka ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-4 py-4 bg-[#1a1a3e]/40 space-y-4 text-xs md:text-sm text-white/90 leading-relaxed">
                  <p>5/1(金)Zepp　Fukuoka『IRINO MIYU LIVE TOUR 2026』にご来場いただいた方を対象に、特典会付き羽田公演リピーターチケットを販売いたします。ご購入特典として、入野自由とのタッチ会に参加いただけます。<br />
                  リピーターチケットの販売は、会場チケット販売窓口の「羽田公演チケット受付」にて行います。</p>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●チケット料金：１階 自由席9,000円（税込）</p>
                    <p className="text-[#fc457e]/90">※リピータ―チケットは１階 自由席のみのお取り扱いとなります</p>
                    <p className="text-[#fc457e]/90">※チケット券種はお選びいただけません。</p>
                    <p className="text-[#fc457e]/90">※本チケットのご購入は現金のみとなります。クレジットカードはご使用いただけません。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●リピーターチケットにつきまして</p>
                    <p>当日16時～予定販売数終了まで「羽田公演チケット販売ブース」にてご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※5/1(金) Zepp　Fukuoka公演にご参加の方のみご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※お一人様ご同行者分含め２枚までご購入いただけます。</p>
                    <p className="text-[#fc457e]/90">※ブースでのチケット販売は数量限定となります。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-[#fc457e] font-bold mb-2">●ご購入特典</p>
                    <p>タッチ会</p>
                    <p className="text-[#fc457e]/90">※お一人様1枚につき、1回ご参加いただけます。</p>
                    <p className="text-[#fc457e]/90">※おひとり様1回でのご参加をお願いしておりますので、並びなおしでの複数回参加などはお断りいたします。</p>
                    <p className="text-[#fc457e]/90">※人数には限りがございますので、上限を超えた場合は先着となります。予めご了承下さい。</p>
                    <p className="text-[#fc457e]/90">※5/1(金) Zepp　Fukuokaにご参加の方で、既に羽田公演の前売りチケットをご購入いただいている方もチケットのご購入履歴が分かる各チケット販売サイトの購入履歴画面や、入金が確認できるメール画面などご提示いただければタッチ会へご参加いただけます。</p>
                    <p className="text-[#fc457e]/90">※羽田公演のチケットをご同行者が購入し、ご自身のお手元に購入証明がないお客様に関しましては購入履歴が分かる画面のスクリーンショットでも対応可能とさせていただきますのでお手元にご用意の上、ご参加をお願いいたします。</p>
                    <p className="text-[#fc457e]/90">※前売りでご購入済みの羽田公演チケット1枚でKanadeviaホール、Zepp Osaka Bayside、Zepp Fukuokaいずれかの会場でタッチ会に１回参加可能となりますので同じチケット1枚で複数の会場でのタッチ会への参加はできません。</p>
                    <p className="text-[#fc457e]/90">※前売りの購入履歴を提示し、Kanadeviaホールで既にタッチ会にご参加いただいたお客様はZepp Osaka Bayside、Zepp Fukuokaでの参加はできません。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">＜特典会 参加方法＞</p>
                    <p>集合時間：終演後当日指定の場所にお集まりください。必ず20:30までにはご整列をお願いいたします。</p>
                    <p>実施時間：整列が完了次第タッチ会開始～実施時間約30分を予定</p>
                    <p>終了予定時間：21:00頃予定</p>
                    <p className="text-[#fc457e]/90">※状況により終了予定時間が前後する可能性がございます。予めご了承くださいませ。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">＜特典会　開催場所＞当日スタッフよりお知らせさせていただきます</p>
                    <p className="text-[#fc457e]/90">※特典会へのご参加されるお客様は、集合時間よりスタッフの指示に従って整列ください。</p>
                    <p className="text-[#fc457e]/90">※スタッフに、公演チケット・ご購入画面をご提示いただきます。</p>
                  </div>
                  <div className="border-t border-[#fc457e]/30 pt-3 space-y-1">
                    <p className="text-white font-bold mb-2">【注意事項】</p>
                    <p className="text-[#fc457e]/90">※参加者多数の場合は、お時間の都合上途中で終了となることがございます。</p>
                    <p className="text-[#fc457e]/90">※特典会に参加されないお客様も多数おられます。どうぞご留意願います。</p>
                    <p className="text-[#fc457e]/90">※通行のためのスペース確保にご協力下さい。</p>
                    <p className="text-[#fc457e]/90">※イベント中スタッフがお客様の肩や腕に触れて誘導する場合がございます。</p>
                    <p className="text-[#fc457e]/90">※イベント会場へのお問い合わせはご遠慮下さい。</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}